import getSymbolFromCurrency from 'currency-symbol-map';
import { dec } from 'shared-components/hoc-components/with-decode-pay-title';
import { getProfileLimitsForCurrency } from '@pano-project/utils/getProfileLimitsForCurrency';
import {
  calculateMaxPossibleProcessingAmount,
  calculateMinPossibleProcessingAmount,
} from 'utils/helper-functions/limits-calculation.ts';
import { resolveBalanceError, INTERIM_BALANCE_MESSAGE_KEY } from 'utils/helper-functions/balance';
import store from '../../state/store';
import { luhnCheck } from './luhn-algorithm';
import { round } from '../round-functions/round';
import { defineDecimalPlaces } from '../round-functions/get-decimal-places';
import { clearSpace } from '../helper-functions/clear-number';
import { wordsCount } from '../helper-functions/words-count/words-count';
import { validateEmail } from './validate-email';
import { validatePhoneNumber } from './validate-phone-number';
import InternalizationService from '../../context-components/internalization';
import { calculateFee } from '../fee-functions/calculate-comission';
import { securityCheck } from '../helper-functions/security-check';
import { eNumber } from '../round-functions/e-number';
import Callbacks from '../../context-components/callbacks';
import { errorMsg } from '../../variables/additional-fields-data';
import { getAdditionalFieldTypeData } from '../additional-fields-functions/get-additional-field-type-data';
import { isUntouchedTemplateValue } from './is-untouched-template-value';
import { getWalletCurrencyLimits } from '../hooks/digital-wallets/helpers.ts';

const i18n = new InternalizationService();
const lang = i18n.lang.bind(i18n);
const langBack = i18n.langBack.bind(i18n);
const langBackWithFallback = i18n.langBackWithFallback.bind(i18n);
const { readCallbacks } = new Callbacks();

// The balance is denominated in the transaction's base currency, while the
// gateway limits are denominated in the processing currency. Both validation
// routines convert the entered amount with the gateway rate before comparing
// it with the balance, so a limit must be converted the same way.
const toBalanceCurrency = (value, rate) => {
  if (value === null || value === undefined || value === '') return null;

  const numericValue = Number(value);
  const conversionRate = Number(rate?.rate);

  if (!Number.isFinite(numericValue) || !Number.isFinite(conversionRate) || conversionRate === 0) {
    return null;
  }

  return numericValue / conversionRate;
};

// Both balance rules share one message, formatted in the transaction currency.
// The two message keys this story specifies do not exist in the backend
// translations payload yet, and an unknown key renders as an empty string, so
// the existing payout message is used as interim text. See T16.
const balanceErrorMessage = (balance, transactionCurrency) =>
  `${lang(INTERIM_BALANCE_MESSAGE_KEY)} ${eNumber(balance)} ${transactionCurrency}`;

const isOptionalEmptyField = (value, fieldName, additional_fields) => {
  if (clearSpace(value)) return false;
  const field = additional_fields?.find((f) => f.name === fieldName);
  if (!field) return false;
  return field.required === false || field.required === 0;
};

export const customValidation = (fields, required, changedField = null) => {
  if (fields === undefined) return {};
  let errors = {};
  const {
    gatewayInfo: { method },
    currentGateway: { additional_fields, payment_method_key },
    payInfo: { template },
    payData: {
      is_customer_email_changed,
      is_email_changed,
      is_customer_phone_changed,
      is_phone_changed,
    },
    profileInfo: { remove_customer_data },
    initTheme: { theme_editor_settings: { customization_settings: { layout } = {} } = {} } = {},
    forceValidationDisable = {},
  } = store.getState();

  const isForceValidationSkip = (el) => {
    const fieldsToSkip = forceValidationDisable[payment_method_key];
    return Array.isArray(fieldsToSkip) && fieldsToSkip.includes(el);
  };

  const isAmountFirstLayout = layout === 'amount-first';

  const changeErrors = (name, value) => {
    errors = {
      ...errors,
      [name]: value,
    };
  };
  const { fieldsValidation = {} } = readCallbacks() || {};
  const fieldChanged = changedField || Object.keys(fields || {})[0];

  if (fieldChanged && Object.keys(fieldsValidation).length) {
    Object.entries(fieldsValidation).forEach(([fieldName, config]) => {
      if (!Array.isArray(config?.dependsOn)) return;
      if (!config.dependsOn.includes(fieldChanged)) return;
      if (fields[fieldName] !== undefined) return;

      const input = document.getElementsByName(fieldName)?.[0];

      if (input) {
        fields[fieldName] = input.value;
      }
    });
  }

  const customerValidation = (el, value) => {
    let error = false;
    if (!fieldsValidation[el]) return;

    const fieldConfig = fieldsValidation[el];
    const { min, max } = fieldConfig;
    if (!isNaN(+min) && value.length < +min) error = lang('minimumLength', { length: min });
    if (!isNaN(+max) && value.length > +max) error = lang('maximumLength', { length: max });

    // Return early if length validation failed
    if (error) return error;

    // Pattern validation
    if (fieldConfig.pattern) {
      try {
        const regex = new RegExp(fieldConfig.pattern);
        if (!regex.test(value)) {
          return fieldConfig.patternError || fieldConfig.customError || lang('invalidInputFormat');
        }
      } catch (err) {
        console.warn('[Validation] Pattern validation error:', err);
      }
    }

    // Custom validation function
    if (typeof fieldConfig.validate === 'function') {
      try {
        const validationResult = fieldConfig.validate(value, fields);

        if (typeof validationResult === 'boolean') {
          if (!validationResult) {
            return fieldConfig.customError || lang('invalidValue');
          }
        } else if (validationResult && typeof validationResult === 'object') {
          if (validationResult.valid === false) {
            return validationResult.error || fieldConfig.customError || lang('invalidValue');
          }
        }
      } catch (err) {
        console.warn('[Validation] Custom validation error:', err);
      }
    }

    return false;
  };

  Object.keys(fields).map((el) => {
    let value = fields[el];

    if (value === undefined) return;
    if (value === null) value = '';

    if (method !== 'card-method' && isUntouchedTemplateValue(template[el], value)) {
      return changeErrors(el, null);
    }

    if (isAmountFirstLayout && isOptionalEmptyField(value, el, additional_fields)) {
      return changeErrors(el, null);
    }

    if (isForceValidationSkip(el)) return changeErrors(el, null);

    if (!clearSpace(value) && required.includes(el)) {
      // for pix_value field, we need to show the error message from the local data
      if (el === 'pix_value') return changeErrors(el, errorMsg);

      return changeErrors(el, langBack(`cannotBeBlank`));
    }

    if (securityCheck(value)) return changeErrors(el, langBack(`Incorrect value`));

    const customValidationError = customerValidation(el, value);
    if (customValidationError) return changeErrors(el, customValidationError);

    if (el === dec(1)) {
      if (fields.template_token || fields.card_token || fields.pci) return changeErrors(el, null);
      if (!luhnCheck(value)) return changeErrors(el, lang('cardIsNotValid'));
      return changeErrors(el, null);
    }
    if (el === dec(2)) {
      if (fields.template_token || fields.card_token) return changeErrors(el, null);
      if (value.length !== 5) return changeErrors(el, lang('invalidDateFormat'));
      else if (value.substring(0, 2) === '00') {
        return changeErrors(el, lang('invalidDateFormat'));
      } else if (value.length === 5) {
        const month = value.slice(0, 2);
        const year = value.slice(3, 5);
        if (+month > 12) return changeErrors(el, lang('invalidDateFormat'));
        const date = new Date(`20${year}`, +month);
        const diff = new Date() - date;
        if (+diff > 0) return changeErrors(el, lang('newInvalidExpireData'));
      }
    }
    if (el === dec(0)) {
      if (value.length < 3) return changeErrors(el, lang(`invalidInputFormat`, { length: 3 }));
    }
    if (el === dec(3)) {
      if (wordsCount(value) < 2) return changeErrors(el, lang(`cardHolderInvalid`));
    }
    if (el === 'country') {
      value = clearSpace(value);
      if (value.length > 2) return changeErrors(el, lang('maximumLength', { length: 2 }));
    }
    if (el === 'email' || el === dec(5)) {
      if (el === dec(5) && remove_customer_data && !is_customer_email_changed)
        return changeErrors(el, null);
      if (
        el === 'email' &&
        remove_customer_data &&
        ((!is_email_changed && value) || is_email_changed === 0)
      )
        return changeErrors(el, null);
      if (customerValidation('email', value))
        return changeErrors(el, customerValidation('email', value));
      value = clearSpace(value);
      if (!value) value = '';
      // Skip validation for masked emails (e.g. "lra***@***.com" from backend)
      if (value.includes('*')) return changeErrors(el, null);
      if (!validateEmail(value)) return changeErrors(el, lang(`emailIsNotValid`));
    }
    if (el === 'phone' || el === dec(4)) {
      if (el === dec(4) && remove_customer_data && !is_customer_phone_changed) {
        return changeErrors(el, null);
      }
      if (
        el === 'phone' &&
        !getAdditionalFieldTypeData(el, additional_fields) &&
        remove_customer_data &&
        !is_phone_changed &&
        ((!is_email_changed && value) || is_email_changed === 0)
      ) {
        return changeErrors(el, null);
      }
      if (customerValidation('phone', value)) {
        return changeErrors(el, customerValidation('phone', value));
      }
      value = clearSpace(value);
      if (!value) value = '';
      // Skip validation for masked phones (e.g. "+35***000" from backend)
      if (value.includes('*')) return changeErrors(el, null);
      if (!validatePhoneNumber(value)) {
        return changeErrors(el, lang(`phoneIsNotValid`));
      }
    }
    if (el === 'city') {
      value = clearSpace(value);
      if (value.length > 50) return changeErrors(el, lang('maximumLength', { length: 50 }));
    }
    if (el === 'state') {
      value = clearSpace(value);
      if (value.length > 50) return changeErrors(el, lang('maximumLength', { length: 50 }));
    }
    if (el === 'zip') {
      value = clearSpace(value);
      if (value.length > 12) return changeErrors(el, lang('maximumLength', { length: 12 }));
    }
    if (el === 'address') {
      value = clearSpace(value);
      if (value.length > 100) return changeErrors(el, lang('maximumLength', { length: 100 }));
    }
    if (el === 'full_name') {
      value = clearSpace(value);
      if (value.length > 100) return changeErrors(el, lang('maximumLength', { length: 100 }));
    }

    // validation for new additional fields by type
    const field = getAdditionalFieldTypeData(el, additional_fields);

    if (field) {
      value = clearSpace(value);
      const isValid = field.validationFunction(value);
      if (!isValid) {
        const error = field.uiTexts.errorTransaltionFallback
          ? langBackWithFallback(field.uiTexts.error, field.uiTexts.errorTransaltionFallback)
          : langBack(field.uiTexts.error);
        return changeErrors(el, error);
      }
    }

    return changeErrors(el, null);
  });
  return errors;
};

// `options.limits` (retry pages only): min/max that replace the raw gateway limits per bound.
// `options.allowMissingRate`: keep checking limits when the currency has no rate entry.
export const validateConvertAmount = (amount, currency, _required, options = {}) => {
  const {
    profileInfo: {
      customer: { balance } = {},
      base_currency,
      transaction_type,
      available_currencies,
    },
    gatewayInfo: { is_auth },
    virtualSettings: { override_limits },
    initTheme: { theme_editor_settings: { customization_settings: { layout } = {} } = {} } = {},
    resultOfPay,
  } = store.getState();
  const {
    currentGateway: { payment_information: { currency_list = [] } = {}, fees },
  } = store.getState();

  const amountErrorKey = base_currency === currency ? 'amount' : 'totalCharge';
  const isAmountFirstRetry =
    layout === 'amount-first' &&
    (!!resultOfPay?.transaction?.retry_settings || !!resultOfPay?.retry_settings);
  let rate = currency_list[currency];
  if (isAmountFirstRetry) {
    const gatewayRate = currency_list[currency];
    const profileLimits = getProfileLimitsForCurrency(currency, available_currencies?.currencies);
    rate = {
      ...(gatewayRate || {}),
      min_amount: gatewayRate?.min_amount ?? profileLimits?.min_amount ?? null,
      max_amount: gatewayRate?.max_amount ?? profileLimits?.max_amount ?? null,
      rate: gatewayRate?.rate ?? profileLimits?.rate ?? 1,
    };
  }

  if (currency === '' || currency === null)
    return {
      currency: lang(`cannotBeBlank`),
      amount: amount === '' || amount === null ? lang(`cannotBeBlank`) : null,
      totalCharge: null,
    };

  const useOptionLimits = !!options.limits;
  if (amount === undefined || (!rate && !(useOptionLimits && options.allowMissingRate))) return {};

  if (!rate) rate = {};

  let min = rate.min_amount;
  let max = rate.max_amount;

  const walletLimits = getWalletCurrencyLimits(currency);
  if (walletLimits) {
    min = walletLimits.min_amount;
    max = walletLimits.max_amount;
  }

  if (options.limits) {
    min = options.limits.min ?? min;
    max = options.limits.max ?? max;
  }

  // Get calculated fee object
  let feeObject =
    fees && fees[currency] ? calculateFee(fees, amount, currency, transaction_type) : null;
  let fee = feeObject ? feeObject.fee : null;

  if (amount === '' || amount === null)
    return { amount: lang(`cannotBeBlank`), currency: null, totalCharge: null };
  if (amount === '.') return { amount: lang('invalidAmount'), currency: null, totalCharge: null };

  const feeIsIncluded = !isAmountFirstRetry ? fee?.is_included : true;
  const amountToCompare =
    layout === 'amount-first' && fee && !feeIsIncluded ? feeObject.total : amount;
  let convertAmount = round(Number(amountToCompare) / rate.rate);

  // The balance rules run before the minimum/maximum limit messages, so a
  // balance problem is never hidden behind a limit message. A missing balance
  // switches the frontend check off; a real zero balance keeps it on.
  if (transaction_type === 'pay-out') {
    const limitInBalanceCurrency =
      !override_limits && !is_auth ? toBalanceCurrency(min, rate) : null;
    const balanceError = resolveBalanceError({
      balance,
      amount: convertAmount,
      minimumAmount: limitInBalanceCurrency,
    });

    if (balanceError) {
      const message = balanceErrorMessage(balance, base_currency);

      return {
        amount: amountErrorKey === 'amount' ? message : null,
        totalCharge: amountErrorKey === 'totalCharge' ? message : null,
        currency: null,
      };
    }
  }

  if (min && +amount < +min && !override_limits && !is_auth) {
    return {
      amount: amountErrorKey === 'amount' ? `${lang('minimumValueIs')} ${eNumber(min)}.` : null,
      totalCharge:
        amountErrorKey === 'totalCharge' ? `${lang('minimumValueIs')} ${eNumber(min)}.` : null,
      currency: null,
    };
  }
  if (max && +amount > +max && !override_limits && !is_auth) {
    return {
      amount: `${lang('maximumValueIs')} ${eNumber(max)}.`,
      totalCharge: null,
      currency: null,
    };
  }

  return { amount: null, currency: null, totalCharge: null };
};

export const validateConvertDepositAmount = (amount, currency) => {
  let {
    profileInfo: { customer: { balance } = {}, base_currency, transaction_type },
    currentGateway,
    payData: { totalCharge },
    virtualSettings: { override_limits },
    gatewayInfo: { is_auth },
    initTheme: { theme_editor_settings: { customization_settings: { layout } = {} } = {} } = {},
  } = store.getState();
  const {
    currentGateway: {
      payment_information: {
        currency_list = [],
        rate: baseRate,
        min_amount,
        crypto,
        max_amount,
      } = {},
      fees,
    },
  } = store.getState();

  const amountErrorKey = base_currency === currency ? 'amount' : 'totalCharge';
  const rate = (layout === 'amount-first'
    ? currency_list[currency] || currency_list[base_currency]
    : currency_list[currency]) || {
    rate: baseRate,
    crypto,
    min_amount,
    max_amount,
  };

  if (currency === '' || currency === null)
    return {
      currency: lang(`cannotBeBlank`),
      amount: amount === '' || amount === null ? lang(`cannotBeBlank`) : null,
    };

  if (totalCharge === '' || totalCharge === null)
    return {
      currency: lang(`cannotBeBlank`),
      totalCharge: totalCharge === '' || totalCharge === null ? lang(`cannotBeBlank`) : totalCharge,
    };

  if (amount === undefined || !rate) return {};

  const isCurrencyConversion = currency !== base_currency;
  let min = isCurrencyConversion
    ? calculateMinPossibleProcessingAmount(currency, currentGateway)
    : rate.min_amount;
  let max = isCurrencyConversion
    ? calculateMaxPossibleProcessingAmount(currency, currentGateway)
    : rate.max_amount;

  const walletLimits = getWalletCurrencyLimits(currency);
  if (walletLimits) {
    min = walletLimits.min_amount;
    max = walletLimits.max_amount;
  }

  // Get calculated fee object
  let feeObject =
    fees && fees[currency] ? calculateFee(fees, amount, currency, transaction_type) : null;
  let fee = feeObject ? feeObject.fee : null;

  if (amount === '' || amount === null) return { amount: lang(`cannotBeBlank`), currency: null };
  if (amount === '.') return { amount: lang('invalidAmount'), currency: null };

  const amountToCompare =
    layout === 'amount-first' && fee && !fee.is_included ? feeObject.total : amount;
  let convertAmount = round(Number(amountToCompare) / rate.rate);

  // The balance rules run before the minimum/maximum limit messages, so a
  // balance problem is never hidden behind a limit message. A missing balance
  // switches the frontend check off; a real zero balance keeps it on.
  if (transaction_type === 'pay-out') {
    const limitInBalanceCurrency =
      !override_limits && !is_auth ? toBalanceCurrency(min, rate) : null;
    const balanceError = resolveBalanceError({
      balance,
      amount: convertAmount === '' ? amountToCompare : convertAmount,
      minimumAmount: limitInBalanceCurrency,
    });

    if (balanceError) {
      return {
        amount: balanceErrorMessage(balance, base_currency),
        currency: null,
      };
    }
  }

  if (min && +amount < +min && !override_limits && !is_auth) {
    return {
      [`${amountErrorKey}`]: lang(
        transaction_type === 'pay-in' ? 'newDepositAmountMustBe' : 'newWithdrawAmountMustBe',
        {
          currency: getSymbolFromCurrency(currency) || '',
          min: eNumber(min),
          max: eNumber(max),
        },
      ),
      currency: null,
    };
  }

  if (max && +amount > +max && !override_limits && !is_auth) {
    return {
      [`${amountErrorKey}`]: lang(
        transaction_type === 'pay-in' ? 'newDepositAmountMustBe' : 'newWithdrawAmountMustBe',
        {
          currency: getSymbolFromCurrency(currency) || '',
          min: eNumber(min),
          max: eNumber(max),
        },
      ),
      currency: null,
    };
  }

  return { amount: null, currency: null, totalCharge: null };
};
